import {
  Box,
  Button,
  Chip,
  FormControl,
  Input,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import TableDashboardObject from './Table';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import useCustomDashboard, {
  IAddCustomDashboardPayload
} from 'src/services/useCustomDashboard';
import {
  reducerUpdateCurrentScreen,
  reducerUpdateDashboardList,
  reducerUpdateObjectManagementDashboardSelectedById
} from 'src/features/customDashboard';
import {
  IDashboardObject,
  TCustomDashboardSourceType
} from 'src/models/customDashboard';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import ModalManageObjectByGroup from '../ModalManageObjectByGroup';
import { IGranularity } from 'src/models/general';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ModalCustomDownload from './components/ModalCustomDownload';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import { useAlert } from 'src/hooks/useAlert';
import ModalCreateCustomDashboard from './components/ModalCreateCustomDashboard';
import { getSourceTypeLabel } from 'src/utils/getSourceTypeLabel';
import { themeColors } from 'src/theme/schemes/PureLightTheme';
import ProductTourTooltip from '../ProductTourTooltip';
import { CUSTOM_DASHBOARD } from 'src/route';
import { useNavigate } from 'react-router';

const CustomDashboardObject = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { handleClickAlert } = useAlert();
  const dispatch = useAppDispatch();
  const { dateRangeFormatted, objectIdList } = useAppSelector(
    (state) => state.analyticArguments
  );
  const { dashboardList, dashboardSelectedById } = useAppSelector(
    (state) => state.storeCustomDashboard
  );
  const projectId = useAppSelector((state) => state.users.project.id);
  const timezone = useAppSelector((state) => state.users.project.timezone);
  const advanceSearchSocialMedia = useAppSelector(
    (state) => state.storeAdvanceSearchSocialMedia
  );
  const dashboardListResponse = useAppSelector(
    (state) => state.storeCustomDashboard.dashboardList
  );
  const fillAdvanceSearchSocialMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchSocialMedia);
  }, [advanceSearchSocialMedia]);

  const {
    addCustomDashboard,
    createDownloadJob,
    resCreateDownloadJob,
    createDownloadJobOnm,
    resCreateDownloadJobOnm,
    createDownloadJobOfm,
    resCreateDownloadJobOfm
  } = useCustomDashboard();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState<string>('');
  const [editingDashboard, setEditingDashboard] = useState<boolean>(false);

  const handleClose = () => setOpen(false);

  const handleOpen = () => setOpen(true);

  const [openAddDashboard, setOpenAddDashboard] = useState(false);
  const handleClickAddDashboard = () => {
    setOpenAddDashboard(true);
  };
  const handleCloseAddDashboard = () => {
    setOpenAddDashboard(false);
  };

  const handleSubmitCreateCustomDashboard = (
    data: IAddCustomDashboardPayload
  ) => {
    addCustomDashboard({
      variables: data,
      onCompleted: (data) => {
        const dashboard: IDashboardObject =
          data.customDashboard_addCustomDashboard;
        dispatch(
          reducerUpdateDashboardList({
            ...dashboardList,
            data: [
              ...dashboardList.data,
              { ...dashboard, customDashboardWidgetMetadataList: [] }
            ]
          })
        );
        dispatch(reducerUpdateObjectManagementDashboardSelectedById(dashboard));
        handleCloseAddDashboard();
      },
      onError: (error) => {
        console.error('Error creating custom dashboard:', error);
      }
    });
  };

  const granularity: IGranularity = {
    unit: 'day',
    value: 1
  };

  const finalPayload = {
    ...fillAdvanceSearchSocialMedia,
    projectId,
    startDate: dateRangeFormatted.start,
    endDate: dateRangeFormatted.end,
    objectIds: objectIdList,
    granularity: granularity,
    timezone,
    customDashboardId: dashboardSelectedById.id
  };

  const handleDownloadDefault = async (isWhiteLabel: boolean) => {
    handleCloseDownload();
    handleCloseCustomDownload();
    try {
      await createDownloadJob({
        variables: {
          ...finalPayload,
          start: finalPayload.startDate,
          end: finalPayload.endDate,
          objectList: finalPayload.objectIds,
          isWhiteLabel
        }
      });
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        severity: 'success',
        message:
          'It may take some time for the report to be processed. The report can be downloaded from the Activity List on the top right corner.'
      });
    } catch (e) {
      console.log(e);
    }
  };

  const handleDownloadOnmOfm = async (isWhiteLabel: boolean) => {
    handleCloseDownload();
    handleCloseCustomDownload();

    const mutateFunction =
      dashboardSelectedById.sourceType === 'onm'
        ? createDownloadJobOnm
        : createDownloadJobOfm;

    const clippingId =
      dashboardSelectedById.sourceType === 'onm'
        ? dashboardSelectedById.clippingOnm.id
        : dashboardSelectedById.clippingOfm.id;

    try {
      await mutateFunction({
        variables: {
          start: dateRangeFormatted.start,
          end: dateRangeFormatted.end,
          customDashboardId: dashboardSelectedById.id,
          granularity: granularity,
          mediaList: [],
          projectId: projectId,
          timezone: timezone,
          clippingId,
          isWhiteLabel
        }
      });
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        severity: 'success',
        message:
          'It may take some time for the report to be processed. The report can be downloaded from the Activity List on the top right corner.'
      });
    } catch (e) {
      console.log(e);
    }
  };

  const dashboardActive = dashboardSelectedById;

  const [anchorElDownload, setAnchorElDownload] =
    useState<HTMLButtonElement | null>(null);

  const handleClickDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElDownload(event.currentTarget);
  };

  const handleCloseDownload = () => {
    setAnchorElDownload(null);
  };

  const openDownload = Boolean(anchorElDownload);
  const id = openDownload ? 'simple-popover' : undefined;

  const [openCustomDownload, setOpenCustomDownload] = useState<boolean>(false);
  const handleOpenCustomDownload = () => {
    setOpenCustomDownload(true);
  };
  const handleCloseCustomDownload = () => {
    setOpenCustomDownload(false);
  };

  const isKompasWhiteLabel = window.location.hostname.includes('kompas');

  const getSourceTypeBgColor = (sourceType: TCustomDashboardSourceType) => {
    switch (sourceType) {
      case 'onm':
        return themeColors.black;
      case 'ofm':
        return themeColors.warning;
      case 'socmed':
        return themeColors.darkTeal;
      default:
        return '#E0F7FA';
    }
  };

  const {
    isOnTour,
    currentTour: { stepNumber, menu }
  } = useAppSelector((state) => state.storeProductTour);

  const isCurrentlyOnTourStep11 =
    stepNumber === 11 && isOnTour && menu === 'customization';

  useEffect(() => {
    if (stepNumber === 0 && dashboardSelectedById.id === 'sample') {
      navigate(CUSTOM_DASHBOARD);
      dispatch(
        reducerUpdateDashboardList({
          ...dashboardListResponse
        })
      );
      dispatch(reducerUpdateObjectManagementDashboardSelectedById(null));
      dispatch(reducerUpdateCurrentScreen('sourceType'));
    }
  }, [stepNumber, dashboardSelectedById]);

  const menuName = timezone === 7 ? 'Custom Dashboard' : 'Instant Insight';

  return (
    <Stack
      direction="row"
      alignItems={'center'}
      justifyContent={'space-between'}
      flexWrap={'wrap'}
      gap={1}
    >
      <Stack
        direction="row"
        flexWrap={'wrap-reverse'}
        spacing={{ md: 2 }}
        gap={{ xs: 1, md: 0 }}
        alignItems="center"
      >
        <FormControl size="small" sx={{ width: { xs: '100%', md: '509px' } }}>
          <Select
            name="group"
            open={open}
            onClose={handleClose}
            onOpen={handleOpen}
            value={dashboardSelectedById?.id ?? ''}
            defaultValue={dashboardSelectedById?.id ?? ''}
          >
            <MenuItem value={''} key={'_default'} sx={{ display: 'none' }} />
            {dashboardList.data.map((dashboard) => (
              <MenuItem
                key={dashboard.id}
                value={dashboard.id}
                sx={{ display: 'none' }}
              >
                {dashboard.displayName}{' '}
                <span>
                  <Chip
                    label={getSourceTypeLabel(dashboard.sourceType)}
                    size="small"
                    sx={{
                      backgroundColor: getSourceTypeBgColor(
                        dashboard.sourceType
                      ),
                      color: '#fff',
                      fontWeight: 500
                    }}
                  />
                </span>
              </MenuItem>
            ))}

            <Box sx={{ p: theme.spacing(2) }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                marginBottom={1}
              >
                <Typography variant="h4">{menuName}</Typography>
              </Stack>
              <Input
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
                fullWidth
                placeholder="Search Source"
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                }}
              />
              <Box sx={{ mt: theme.spacing(1) }}>
                <TableDashboardObject
                  dashboardObjects={dashboardList.data}
                  loading={dashboardList.loading}
                  onClick={handleClose}
                />
              </Box>
            </Box>
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          endIcon={<AddIcon />}
          sx={{
            borderRadius: '6px',
            minWidth: theme.spacing(6),
            width: { xs: '100%', md: 'auto' },
            height: 36
          }}
          onClick={(event) => {
            handleClickAddDashboard();
            event.stopPropagation();
            event.preventDefault();
          }}
        >
          Create New
        </Button>
      </Stack>
      <ProductTourTooltip open={isCurrentlyOnTourStep11}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<DownloadIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            minWidth: theme.spacing(6),
            height: 36,
            backgroundColor: '#FBFBFB',
            borderRadius: theme.spacing(1),
            ...(isCurrentlyOnTourStep11
              ? {
                  position: 'relative',
                  zIndex: 1000
                }
              : {})
          }}
          onClick={handleClickDownload}
          disabled={
            resCreateDownloadJob.loading ||
            dashboardSelectedById === null ||
            !dashboardActive ||
            dashboardActive.customDashboardWidgetMetadataList.length < 1
          }
        >
          Download Report
        </Button>
      </ProductTourTooltip>
      {openAddDashboard && (
        <ModalCreateCustomDashboard
          open={openAddDashboard}
          onClose={handleCloseAddDashboard}
          onSubmit={handleSubmitCreateCustomDashboard}
        />
      )}
      <ModalManageObjectByGroup
        title="Object"
        isHidden={true}
        onCloseEdit={() => setEditingDashboard(false)}
        isEditing={editingDashboard}
        isDashboard={true}
      />

      <Popover
        id={id}
        open={openDownload}
        anchorEl={anchorElDownload}
        onClose={handleCloseDownload}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <List>
          {!isKompasWhiteLabel ? (
            <ListItemButton
              onClick={
                !isCurrentlyOnTourStep11
                  ? () =>
                      dashboardSelectedById.sourceType === 'socmed'
                        ? handleDownloadDefault(false)
                        : handleDownloadOnmOfm(false)
                  : undefined
              }
            >
              <ListItemText primary="Download (PDF)" />
            </ListItemButton>
          ) : null}
          <ListItemButton
            onClick={
              !isCurrentlyOnTourStep11 ? handleOpenCustomDownload : undefined
            }
          >
            <ListItemText primary="White-label Report" />
          </ListItemButton>
        </List>
      </Popover>
      <ModalCustomDownload
        open={openCustomDownload}
        onClose={handleCloseCustomDownload}
        onDownload={() =>
          !isCurrentlyOnTourStep11
            ? dashboardSelectedById.sourceType === 'socmed'
              ? handleDownloadDefault(false)
              : handleDownloadOnmOfm(false)
            : undefined
        }
      />
    </Stack>
  );
};

export default CustomDashboardObject;
